import { useEffect, useState } from "react";
import { Rocket, Server, Mail, Activity, ArrowRight, Sparkles, CreditCard } from "lucide-react";
import api from "@/lib/api";

const PILLARS = [
  { icon: Server, title: "Full-stack build & hosting", desc: "Database, auth, APIs and file storage — and you can point your own domain right at it. A complete stack, not just a pretty front end." },
  { icon: CreditCard, title: "Payments built in", desc: "Take one-time payments and subscriptions with secure Stripe checkout and signed webhooks — sell products, memberships or bookings straight from your site." },
  { icon: Activity, title: "Live visitor analytics", desc: "See who's on your site, what they click, and where they are — built in, not bolted on." },
  { icon: Mail, title: "Best-value business email", email: true, desc: "Emergent runs the app; for your inbox we steer you to these — cheaper than registrar email:" },
];

export default function EmergentBuildPromo({ domain, variant = "full" }) {
  const [ref, setRef] = useState("https://app.emergent.sh");
  const [emailPartners, setEmailPartners] = useState([]);

  useEffect(() => {
    let done = false;
    api.get("/plans").then((r) => {
      if (done) return;
      if (r.data?.emergent_referral_url) setRef(r.data.emergent_referral_url);
      if (Array.isArray(r.data?.email_partners)) setEmailPartners(r.data.email_partners);
    }).catch(() => {});
    return () => { done = true; };
  }, []);

  return (
    <div
      data-testid="emergent-build-promo"
      className="relative overflow-hidden rounded-2xl p-[1px] mt-6"
      style={{ background: "linear-gradient(135deg, #007AFF, #9b5cff 55%, #ff5c8a)" }}
    >
      <div className="relative rounded-2xl bg-[#0d0d0f] p-6 sm:p-8">
        <div className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full blur-3xl opacity-30" style={{ background: "radial-gradient(circle,#007AFF,transparent 70%)" }} />

        <div className="relative">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#4da3ff] bg-[#007AFF]/10 border border-[#007AFF]/30 rounded-full px-3 py-1">
            <Sparkles size={12} /> Scan → Handoff → Build
          </span>

          <h3 className="font-heading font-bold text-white text-2xl sm:text-3xl mt-4 leading-tight">
            Don't just audit it — <span className="brand-gradient">rebuild it on Emergent.</span>
          </h3>
          <p className="text-zinc-400 text-sm sm:text-base mt-2 max-w-2xl">
            {domain ? <>Take everything we found on <span className="text-zinc-200 font-medium">{domain}</span> and </> : "Take your scan and "}
            hand it to Emergent's AI build agent to ship a faster, secure new site — a real backend with
            payments, live visitor analytics, and your own domain pointed right at it. For the pieces Emergent doesn't run
            (like your business inbox), we point you to the best-value partners.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
            {PILLARS.map((p) => (
              <div key={p.title} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="w-9 h-9 rounded-lg grid place-items-center bg-[#007AFF]/15 border border-[#007AFF]/30 text-[#4da3ff] mb-3">
                  <p.icon size={18} />
                </div>
                <div className="text-white font-semibold text-sm">{p.title}</div>
                <p className="text-zinc-500 text-xs mt-1 leading-relaxed">{p.desc}</p>
                {p.email && emailPartners.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2.5">
                    {emailPartners.map((v) => (
                      <a
                        key={v.name}
                        href={v.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-testid={`promo-email-${v.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="rounded-full border border-[#007AFF]/30 bg-[#007AFF]/10 px-2.5 py-1 text-[11px] font-medium text-[#4da3ff] hover:bg-[#007AFF]/20 hover:text-white transition-colors"
                      >
                        {v.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-6">
            <a
              href={ref}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="emergent-build-cta"
              className="inline-flex items-center justify-center gap-2 btn-brand text-white font-semibold rounded-xl px-6 py-3 text-sm transition-transform hover:scale-[1.02]"
            >
              <Rocket size={16} /> Build it on Emergent <ArrowRight size={16} />
            </a>
            <span className="text-zinc-500 text-xs sm:max-w-xs">
              Tip: generate an <span className="text-zinc-300">AI Build Brief</span> below and hand it straight to Emergent — it becomes your build spec.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
